import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class BulkPricingPopupController {
  #popup;
  #abortController = new AbortController();
  #formObserver;
  #globoInitWrapped = false;
  #applyTimeout;

  constructor(popup) {
    this.#popup = popup;
  }

  init() {
    const section = this.#popup.closest('.shopify-section');
    const productId = this.#popup.dataset.productIdSource;

    if (section instanceof HTMLElement && productId) {
      section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
        signal: this.#abortController.signal,
      });
    }

    this.#observeFormFields();
    this.#attachGloboHooks();
    this.#popup.addEventListener('dialog:open', this.#handleDialogOpen, {
      signal: this.#abortController.signal,
    });
    this.#scheduleApply();
  }

  destroy() {
    this.#abortController.abort();
    this.#formObserver?.disconnect();
    if (this.#applyTimeout) {
      clearTimeout(this.#applyTimeout);
    }
  }

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.#popup.dataset.productIdSource) return;

    const blockId = this.#popup.querySelector('bulk-pricing-popup-product')?.dataset.blockId;
    if (!blockId) return;

    // Reuse the server-rendered summary so the open drawer stays aligned with Shopify pricing and media state.
    const updatedSummary = event.detail.data.html.querySelector(
      `[data-bulk-pricing-summary][data-block-id="${blockId}"]`
    );
    const existingSummary = this.#popup.querySelector('bulk-pricing-popup-product');

    if (!(existingSummary instanceof HTMLElement) || !updatedSummary) return;

    morph(existingSummary, updatedSummary);
    this.#scheduleApply();
  };

  #observeFormFields() {
    // Globo renders the form asynchronously, so keep watching until the relevant controls exist.
    this.#formObserver = new MutationObserver(() => {
      this.#scheduleApply();
    });

    this.#formObserver.observe(this.#popup, {
      childList: true,
      subtree: true,
    });
  }

  #attachGloboHooks() {
    document.addEventListener('globo.formbuilder.scripts.loaded', this.#handleGloboScriptsLoaded, {
      signal: this.#abortController.signal,
    });

    // If Globo is already on the page when this element connects, wrap immediately.
    this.#wrapGloboInit();
  }

  #handleGloboScriptsLoaded = () => {
    this.#wrapGloboInit();
    this.#scheduleApply();
  };

  #wrapGloboInit() {
    const formBuilder = window.Globo?.FormBuilder;
    if (!formBuilder?.init || formBuilder.__bulkPricingPopupWrapped) return;

    const originalInit = formBuilder.init;

    formBuilder.init = (...args) => {
      const result = originalInit.apply(formBuilder, args);

      requestAnimationFrame(() => {
        document.querySelectorAll('.bulk-pricing-popup').forEach((popup) => {
          const controller = popupControllers.get(popup);
          if (controller instanceof BulkPricingPopupController) {
            controller.#scheduleApply();
          }
        });
      });

      return result;
    };

    formBuilder.__bulkPricingPopupWrapped = true;
    this.#globoInitWrapped = true;
  }

  #scheduleApply() {
    requestAnimationFrame(() => {
      this.#applyFormCustomizations();
    });
  }

  #handleDialogOpen = () => {
    this.#scheduleRepeatedApply();
  };

  #scheduleRepeatedApply(iteration = 0) {
    this.#applyFormCustomizations();

    if (iteration >= 12) return;

    this.#applyTimeout = window.setTimeout(() => {
      this.#scheduleRepeatedApply(iteration + 1);
    }, 150);
  }

  #applyFormCustomizations = () => {
    const productData = this.#getProductData();
    if (!productData) return;

    this.#populateProductField(this.#popup, productData);
    this.#populateSizeField(this.#popup, productData);
  };

  #getProductData() {
    try {
      const optionSources = this.#popup.dataset.optionSources ? JSON.parse(this.#popup.dataset.optionSources) : [];
      const explicitSizeOptions = this.#popup.dataset.sizeOptionValues
        ? JSON.parse(this.#popup.dataset.sizeOptionValues)
        : [];
      const domFallback = this.#getProductDataFromDom();
      const hasProductSummary = this.#popup.querySelector('bulk-pricing-popup-product') instanceof HTMLElement;
      // Collection toolbar popups never have a canonical product context, so size should stay free-text there.
      const isCollectionToolbar = this.#popup.dataset.context === 'collection-toolbar';
      const isProductPage =
        !isCollectionToolbar &&
        (this.#popup.dataset.isProductPage === 'true' || hasProductSummary || domFallback?.hasVariantPicker);

      let sizeOptionValues = explicitSizeOptions;

      if (!Array.isArray(sizeOptionValues) || !sizeOptionValues.length) {
        const matchedSizeOption = optionSources.find((option) => option?.name?.toLowerCase?.() === 'size');
        if (Array.isArray(matchedSizeOption?.values) && matchedSizeOption.values.length) {
          sizeOptionValues = matchedSizeOption.values;
        }
      }

      let productTitle = this.#popup.dataset.productTitle || '';
      let selectedSize = this.#popup.dataset.selectedSize || '';

      if (!productTitle || !sizeOptionValues.length) {
        if (domFallback) {
          if (!productTitle) {
            productTitle = domFallback.productTitle;
          }
          if (!sizeOptionValues.length) {
            sizeOptionValues = domFallback.sizeOptionValues;
          }
          if (!selectedSize) {
            selectedSize = domFallback.selectedSize;
          }
        }
      }

      const productData = {
        isProductPage,
        productTitle,
        sizeOptionValues,
        selectedSize,
      };

      return productData;
    } catch {
      return null;
    }
  }

  #getProductDataFromDom() {
    const section = this.#popup.closest('.shopify-section');
    const summaryTitle = this.#popup.querySelector('.bulk-pricing-popup__product-title')?.textContent?.trim();
    const pageTitle =
      section?.querySelector('.view-product-title a')?.textContent?.trim() ||
      document.querySelector('h1')?.textContent?.trim() ||
      '';

    const productTitle = summaryTitle || pageTitle;
    const productId = this.#popup.dataset.productIdSource;
    const variantPicker = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : null;

    if (!(variantPicker instanceof HTMLElement)) {
      return {
        hasVariantPicker: false,
        productTitle,
        sizeOptionValues: [],
        selectedSize: '',
      };
    }

    const optionGroups = Array.from(variantPicker.querySelectorAll('.variant-option'));
    const sizeGroup = optionGroups.find((group) => this.#optionGroupLooksLikeSize(group));

    if (!(sizeGroup instanceof HTMLElement)) {
      return {
        hasVariantPicker: true,
        productTitle,
        sizeOptionValues: [],
        selectedSize: '',
      };
    }

    const dropdown = sizeGroup.querySelector('select');
    if (dropdown instanceof HTMLSelectElement) {
      const options = Array.from(dropdown.options)
        .filter((option) => option.value && !option.disabled)
        .map((option) => option.value.trim());

      const selectedSize =
        dropdown.selectedOptions[0]?.value && !dropdown.selectedOptions[0].disabled
          ? dropdown.selectedOptions[0].value.trim()
          : '';

      return {
        hasVariantPicker: true,
        productTitle,
        sizeOptionValues: options,
        selectedSize,
      };
    }

    const radioInputs = Array.from(sizeGroup.querySelectorAll('input[type="radio"]'));
    const sizeOptionValues = radioInputs
      .map((input) => input.value?.trim())
      .filter(Boolean);

    const checkedInput = radioInputs.find((input) => input.checked);
    const selectedSize = checkedInput?.value?.trim() || '';

    return {
      hasVariantPicker: true,
      productTitle,
      sizeOptionValues,
      selectedSize,
    };
  }

  #optionGroupLooksLikeSize(group) {
    if (!(group instanceof HTMLElement)) return false;

    const labelText =
      group.querySelector('legend')?.textContent?.trim().toLowerCase() ||
      group.querySelector('label')?.textContent?.trim().toLowerCase() ||
      '';

    return labelText.includes('size');
  }

  #populateProductField(popup, productData) {
    const productWrapper = popup.querySelector('.bulk-pricing-product');
    const productField = productWrapper?.querySelector('input, textarea, select');
    const form = popup.querySelector('form');

    if (!(productWrapper instanceof HTMLElement) || !(productField instanceof HTMLElement) || !(form instanceof HTMLFormElement))
      return;

    if (!productData.isProductPage) {
      // Collection toolbar usage needs a visible product field so shoppers can describe the item they want.
      this.#showFieldWrapper(productWrapper);
      productField.disabled = false;
      productField.style.removeProperty('display');
      productField.removeAttribute('aria-hidden');
      this.#removeHiddenSubmissionField(form, 'bulk-pricing-product-value');
      return;
    }

    productWrapper.hidden = true;
    productWrapper.style.setProperty('display', 'none', 'important');
    productWrapper.setAttribute('aria-hidden', 'true');
    productField.disabled = true;

    const submittedProductField = this.#ensureHiddenSubmissionField(
      form,
      productField.getAttribute('name'),
      'bulk-pricing-product-value'
    );

    if (!(submittedProductField instanceof HTMLInputElement)) return;

    submittedProductField.value = productData.productTitle || '';
  }

  #populateSizeField(popup, productData) {
    const sizeWrapper = popup.querySelector('.bulk-pricing-size-selector');
    const sizeInputContainer = sizeWrapper?.querySelector('.globo-form-input');
    const originalSizeField = sizeInputContainer?.querySelector(
      'select:not([data-bulk-pricing-size-select]), input:not([data-bulk-pricing-size-input]), textarea:not([data-bulk-pricing-size-input])'
    );
    const form = popup.querySelector('form');

    if (
      !(sizeWrapper instanceof HTMLElement) ||
      !(sizeInputContainer instanceof HTMLElement) ||
      !(originalSizeField instanceof HTMLElement) ||
      !(form instanceof HTMLFormElement) ||
      !Array.isArray(productData.sizeOptionValues)
    ) {
      return;
    }
    const submittedSizeField = this.#ensureHiddenSubmissionField(
      form,
      originalSizeField.getAttribute('name'),
      'bulk-pricing-size-value'
    );

    if (!(submittedSizeField instanceof HTMLInputElement)) return;

    // Keep Globo's original field out of the submission so our controlled value wins.
    originalSizeField.disabled = true;
    originalSizeField.style.display = 'none';
    originalSizeField.setAttribute('aria-hidden', 'true');

    if (productData.isProductPage && !productData.sizeOptionValues.length) {
      this.#removeCustomSizeField(sizeInputContainer, 'select');
      this.#removeCustomSizeField(sizeInputContainer, 'input');
      submittedSizeField.value = '';
      this.#hideFieldWrapper(sizeWrapper);
      return;
    }

    if (!productData.isProductPage) {
      this.#showFieldWrapper(sizeWrapper);
      this.#renderCustomSizeTextField(
        sizeWrapper,
        sizeInputContainer,
        originalSizeField,
        submittedSizeField,
        productData
      );
      return;
    }

    this.#showFieldWrapper(sizeWrapper);
    this.#removeCustomSizeField(sizeInputContainer, 'input');
    this.#renderCustomSizeSelect(
      sizeWrapper,
      sizeInputContainer,
      originalSizeField,
      submittedSizeField,
      productData
    );
  }

  #renderCustomSizeSelect(
    sizeWrapper,
    sizeInputContainer,
    originalSizeField,
    submittedSizeField,
    productData
  ) {
    if (!(originalSizeField instanceof HTMLSelectElement)) return;

    const serializedOptions = JSON.stringify(productData.sizeOptionValues);
    let customSizeSelect = sizeInputContainer.querySelector('[data-bulk-pricing-size-select]');

    if (!(customSizeSelect instanceof HTMLSelectElement)) {
      customSizeSelect = originalSizeField.cloneNode(false);
      customSizeSelect.removeAttribute('name');
      customSizeSelect.id = `${originalSizeField.id || 'bulk-pricing-size'}-bulk-pricing`;
      customSizeSelect.dataset.bulkPricingSizeSelect = 'true';
      customSizeSelect.disabled = false;
      customSizeSelect.hidden = false;
      customSizeSelect.removeAttribute('aria-hidden');
      customSizeSelect.style.removeProperty('display');

      const sizeLabel = sizeWrapper.querySelector('label');
      if (sizeLabel instanceof HTMLLabelElement) {
        sizeLabel.htmlFor = customSizeSelect.id;
      }

      customSizeSelect.addEventListener('change', () => {
        submittedSizeField.value = customSizeSelect.value;
      });

      sizeInputContainer.append(customSizeSelect);
    }

    if (customSizeSelect.dataset.bulkPricingOptions === serializedOptions) return;

    customSizeSelect.disabled = false;
    customSizeSelect.hidden = false;
    customSizeSelect.removeAttribute('aria-hidden');
    customSizeSelect.style.removeProperty('display');

    const placeholderSource = originalSizeField.querySelector('option[value=""]');
    const placeholderOption = new Option(
      placeholderSource?.textContent?.trim() || 'Please select size',
      ''
    );
    placeholderOption.disabled = true;
    placeholderOption.selected = true;

    customSizeSelect.innerHTML = '';
    customSizeSelect.append(placeholderOption);

    productData.sizeOptionValues.forEach((value) => {
      customSizeSelect.append(new Option(value, value));
    });

    customSizeSelect.value = '';
    customSizeSelect.dataset.bulkPricingOptions = serializedOptions;
    submittedSizeField.value = '';
  }

  #renderCustomSizeTextField(
    sizeWrapper,
    sizeInputContainer,
    originalSizeField,
    submittedSizeField,
    productData
  ) {
    this.#removeCustomSizeField(sizeInputContainer, 'select');

    let customSizeInput = sizeInputContainer.querySelector('[data-bulk-pricing-size-input]');

    if (!(customSizeInput instanceof HTMLInputElement)) {
      customSizeInput = document.createElement('input');
      customSizeInput.type = 'text';
      customSizeInput.className = originalSizeField.className;
      customSizeInput.id = `${originalSizeField.id || 'bulk-pricing-size'}-bulk-pricing-text`;
      customSizeInput.dataset.bulkPricingSizeInput = 'true';
      customSizeInput.autocomplete = 'off';
      customSizeInput.required = originalSizeField.hasAttribute('required');

      const sizeLabel = sizeWrapper.querySelector('label');
      if (sizeLabel instanceof HTMLLabelElement) {
        sizeLabel.htmlFor = customSizeInput.id;
      }

      customSizeInput.addEventListener('input', () => {
        customSizeInput.dataset.bulkPricingDirty = 'true';
        submittedSizeField.value = customSizeInput.value;
      });

      customSizeInput.addEventListener('change', () => {
        submittedSizeField.value = customSizeInput.value;
      });

      sizeInputContainer.append(customSizeInput);
    }

    const placeholderOption =
      originalSizeField instanceof HTMLSelectElement
        ? originalSizeField.querySelector('option[value=""]')?.textContent?.trim()
        : '';
    customSizeInput.placeholder =
      originalSizeField.getAttribute('placeholder') || placeholderOption || 'Enter size';

    // Preserve the shopper's manual entry across Globo re-renders once they start typing.
    if (customSizeInput.dataset.bulkPricingDirty === 'true') return;

    const initialValue = submittedSizeField.value || productData.selectedSize || '';
    customSizeInput.value = initialValue;
    submittedSizeField.value = initialValue;
  }

  #removeCustomSizeField(sizeInputContainer, type) {
    const selector =
      type === 'select' ? '[data-bulk-pricing-size-select]' : '[data-bulk-pricing-size-input]';
    sizeInputContainer.querySelector(selector)?.remove();
  }

  #hideFieldWrapper(fieldWrapper) {
    fieldWrapper.hidden = true;
    fieldWrapper.style.setProperty('display', 'none', 'important');
    fieldWrapper.setAttribute('aria-hidden', 'true');
  }

  #showFieldWrapper(fieldWrapper) {
    fieldWrapper.hidden = false;
    fieldWrapper.style.removeProperty('display');
    fieldWrapper.removeAttribute('aria-hidden');
  }

  #ensureHiddenSubmissionField(form, name, marker) {
    if (!name) return null;

    let hiddenField = form.querySelector(`[data-${marker}]`);

    if (!(hiddenField instanceof HTMLInputElement)) {
      hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = name;
      hiddenField.setAttribute(`data-${marker}`, 'true');
      form.append(hiddenField);
    }

    return hiddenField;
  }

  #removeHiddenSubmissionField(form, marker) {
    form.querySelector(`[data-${marker}]`)?.remove();
  }
}

const popupControllers = new WeakMap();

const initBulkPricingPopups = (root = document) => {
  const popupNodes =
    root instanceof Element && root.matches('.bulk-pricing-popup')
      ? [root]
      : root.querySelectorAll?.('.bulk-pricing-popup') || [];

  popupNodes.forEach((popup) => {
    if (!(popup instanceof HTMLElement) || popupControllers.has(popup)) return;

    const controller = new BulkPricingPopupController(popup);
    popupControllers.set(popup, controller);
    controller.init();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initBulkPricingPopups();
    },
    { once: true }
  );
} else {
  initBulkPricingPopups();
}

document.addEventListener('shopify:section:load', (event) => {
  initBulkPricingPopups(event.target);
});
